import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { deal, shoe, shuffle, type Card, type Rank } from '@campfire/cards';
import { openWindow, respond, type ChallengeWindow } from '@campfire/bluff';

/** Claimed ranks run Aces, 2s … Kings, then wrap. */
export const CHEAT_RANKS: readonly Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

export interface CheatOptions {
  readonly decks: 1 | 2;
  /** Table limit: after this many claims the fewest cards wins. null = play until someone empties their hand. */
  readonly turnLimit: number | null;
}

export interface Reveal {
  readonly claimant: Seat;
  readonly caller: Seat;
  readonly rank: Rank;
  readonly cards: readonly Card[];
  readonly truthful: boolean;
  /** Who picked up the pile. */
  readonly taker: Seat;
  readonly taken: number;
}

export type CheatEvent =
  | { readonly kind: 'claim'; readonly seat: Seat; readonly rank: Rank; readonly count: number }
  | { readonly kind: 'accepted'; readonly seat: Seat; readonly rank: Rank; readonly count: number }
  | ({ readonly kind: 'called' } & Reveal);

export interface CheatState {
  readonly options: CheatOptions;
  readonly hands: readonly (readonly Card[])[];
  /** Face-down discard pile; index 0 is the top. */
  readonly pile: readonly Card[];
  readonly turn: Seat;
  /** Index into CHEAT_RANKS of the rank the current player must claim. */
  readonly rankIndex: number;
  readonly claims: number;
  readonly challenge: ChallengeWindow<Card> | null;
  readonly last: CheatEvent | null;
  readonly result: Outcome | null;
}

export type CheatAction =
  | { readonly type: 'play'; readonly cards: readonly string[] }
  | { readonly type: 'call' }
  | { readonly type: 'pass' };

export interface CheatView {
  readonly you: Seat | null;
  readonly hand: readonly Card[] | null;
  readonly handCounts: readonly number[];
  readonly pileCount: number;
  readonly turn: Seat;
  /** Rank the player on turn must claim. */
  readonly rank: Rank;
  readonly maxPlay: number;
  /** An open claim others may call; its cards stay hidden. */
  readonly claim: { readonly seat: Seat; readonly rank: Rank; readonly count: number; readonly waiting: readonly Seat[] } | null;
  readonly last: CheatEvent | null;
  readonly claims: number;
  readonly turnLimit: number | null;
  readonly result: Outcome | null;
}

const rankAt = (i: number): Rank => CHEAT_RANKS[i % CHEAT_RANKS.length] as Rank;
const maxPlay = (s: CheatState) => 4 * s.options.decks;

function fewestCards(hands: readonly (readonly Card[])[]): Outcome {
  const min = Math.min(...hands.map((h) => h.length));
  const winners = hands.flatMap((h, i) => (h.length === min ? [i] : []));
  const scores = Object.fromEntries(hands.map((h, i) => [i, -h.length]));
  return { winners, draw: false, reason: 'turn-limit', scores };
}

/** Claim settled: check for a winner, the table limit, then move to the next player and rank. */
function nextTurn(s: CheatState): CheatState {
  const claimant = s.turn;
  if ((s.hands[claimant]?.length ?? 0) === 0) {
    return { ...s, challenge: null, result: { winners: [claimant], draw: false, reason: 'empty-hand' } };
  }
  const claims = s.claims + 1;
  if (s.options.turnLimit !== null && claims >= s.options.turnLimit) {
    return { ...s, claims, challenge: null, result: fewestCards(s.hands) };
  }
  return { ...s, claims, challenge: null, turn: (claimant + 1) % s.hands.length, rankIndex: s.rankIndex + 1 };
}

const holdsAll = (hand: readonly Card[], ids: readonly string[]) => ids.every((id) => hand.some((c) => c.id === id));

export const cheat: GameDefinition<CheatState, CheatAction, CheatView, CheatOptions> = {
  id: 'cheat',
  name: 'Cheat (BS / Bluff)',
  rules: [
    'Be the first to get rid of all your cards. Also known as BS or Bluff.',
    'The whole deck is dealt out, so hands may be uneven. Seat 1 starts.',
    'Each turn has a rank you must claim: Aces first, then 2s, 3s and so on up to Kings, then round again. You do not choose it.',
    'Put 1 to 4 cards face down (up to 8 with two decks) and the app announces them as that rank. They can be anything, so you can lie.',
    'After a play, any other player may Call it. If you are not calling, tap Pass. The play stands once everybody has passed.',
    'If the call is right (at least one card was not the claimed rank) the player who played picks up the whole pile. If the cards were all honest, the caller picks it up.',
    'You win when you play your last card and it survives the challenge; if someone calls it and you were bluffing, you pick up the pile and play on.',
    'Settings: Decks (one or two) and Table limit. With a limit, the game ends after 52 or 104 plays and the fewest cards wins; ties share the win.',
    'The app checks that you hold the cards you play. Whether you really want to bluff or call is up to you; there are no other house rules to agree.',
  ],
  minSeats: 3,
  maxSeats: 10,
  defaultOptions: { decks: 1, turnLimit: null },
  optionSpecs: [
    {
      key: 'decks',
      label: 'Decks',
      help: 'Use two decks for big tables; up to 8 cards may then be played at once.',
      choices: [
        { value: 1, label: 'One deck' },
        { value: 2, label: 'Two decks' },
      ],
    },
    {
      key: 'turnLimit',
      label: 'Table limit',
      help: 'End after this many plays; fewest cards wins.',
      choices: [
        { value: null, label: 'None — first to empty their hand' },
        { value: 52, label: '52 plays' },
        { value: 104, label: '104 plays' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    const { hands } = deal(shuffle(shoe(options.decks), rng), seats, { perHand: 'all' });
    return { options, hands, pile: [], turn: 0, rankIndex: 0, claims: 0, challenge: null, last: null, result: null };
  },

  activeSeats: (s) => (s.result ? [] : s.challenge ? s.challenge.waiting : [s.turn]),

  validate(s, a, seat) {
    if (a.type === 'play') {
      if (s.challenge) return { ok: false, reason: 'waiting for players to call or let it go' };
      if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
      const ids = Array.isArray(a.cards) ? a.cards : [];
      if (ids.length < 1 || ids.length > maxPlay(s)) return { ok: false, reason: `play 1-${maxPlay(s)} cards` };
      if (new Set(ids).size !== ids.length || !holdsAll(s.hands[seat] ?? [], ids)) return { ok: false, reason: 'you do not hold those cards' };
      return { ok: true };
    }
    if (a.type === 'call' || a.type === 'pass') {
      if (!s.challenge) return { ok: false, reason: 'nothing to call' };
      if (!s.challenge.waiting.includes(seat)) return { ok: false, reason: 'not yours to call' };
      return { ok: true };
    }
    return { ok: false, reason: 'unknown action' };
  },

  apply(s, a, { actor }) {
    const seat = actor as Seat;
    if (a.type === 'play') {
      const ids = new Set(a.cards);
      const hand = s.hands[seat] as readonly Card[];
      const played = hand.filter((c) => ids.has(c.id));
      const rank = rankAt(s.rankIndex);
      return {
        ...s,
        hands: s.hands.map((h, i) => (i === seat ? h.filter((c) => !ids.has(c.id)) : h)),
        pile: [...played, ...s.pile],
        challenge: openWindow({ claimant: seat, claimed: rank, items: played }, s.hands.length),
        last: { kind: 'claim', seat, rank, count: played.length },
      };
    }
    const w = s.challenge as ChallengeWindow<Card>;
    const step = respond(w, seat, a.type === 'call' ? 'call' : 'pass', (c, claimed) => c.rank === claimed);
    if (step.kind === 'open') return { ...s, challenge: step.window };
    const rank = step.claim.claimed as Rank;
    if (step.kind === 'accepted') {
      return nextTurn({ ...s, last: { kind: 'accepted', seat: step.claim.claimant, rank, count: step.claim.items.length } });
    }
    const taker = step.truthful ? step.caller : step.claim.claimant;
    return nextTurn({
      ...s,
      hands: s.hands.map((h, i) => (i === taker ? [...h, ...s.pile] : h)),
      pile: [],
      last: {
        kind: 'called',
        claimant: step.claim.claimant,
        caller: step.caller,
        rank,
        cards: step.claim.items,
        truthful: step.truthful,
        taker,
        taken: s.pile.length,
      },
    });
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const w = s.challenge;
    return {
      you,
      hand: you === null ? null : (s.hands[you] ?? null),
      handCounts: s.hands.map((h) => h.length),
      pileCount: s.pile.length,
      turn: s.turn,
      rank: rankAt(s.rankIndex),
      maxPlay: maxPlay(s),
      claim: w ? { seat: w.claim.claimant, rank: w.claim.claimed as Rank, count: w.claim.items.length, waiting: w.waiting } : null,
      last: s.last,
      claims: s.claims,
      turnLimit: s.options.turnLimit,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result) return [];
    if (s.challenge) return s.challenge.waiting.includes(seat) ? [{ type: 'call' }, { type: 'pass' }] : [];
    if (seat !== s.turn) return [];
    const hand = s.hands[seat] ?? [];
    const rank = rankAt(s.rankIndex);
    const honest = hand.filter((c) => c.rank === rank).map((c) => c.id);
    const others = hand.filter((c) => c.rank !== rank).map((c) => c.id);
    const out = new Map<string, CheatAction>();
    const add = (ids: readonly string[]) => ids.length > 0 && out.set([...ids].sort().join(), { type: 'play', cards: ids });
    // A representative subset: every honest count, plus bluffs of each size (whole-power-set is huge).
    for (let k = 1; k <= Math.min(maxPlay(s), hand.length); k++) {
      if (honest.length >= k) add(honest.slice(0, k));
      if (others.length >= k) add(others.slice(0, k));
      if (honest.length >= 1 && others.length >= k - 1 && k > 1) add([...honest.slice(0, 1), ...others.slice(0, k - 1)]);
    }
    return [...out.values()];
  },
};
